import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { InfoTip } from "@/components/InfoTip";
import { ListChecks, ArrowRight, Loader2 } from "lucide-react";

// Evidence Coverage entry-point card for the property dashboard (FREE).
export function CoverageCard({ propertyId }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let ok = true;
    api.get(`/properties/${propertyId}/coverage`)
      .then((r) => ok && setD(r.data))
      .catch(() => ok && setD(false))
      .finally(() => ok && setLoading(false));
    return () => { ok = false; };
  }, [propertyId]);

  if (loading) return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4 flex items-center justify-center" data-testid="coverage-card-loading">
      <Loader2 className="animate-spin text-brand" size={18} />
    </div>
  );
  if (!d) return null;
  const s = d.summary;
  const phaseLabel = d.phase === "move_in" ? "Move-in" : "Move-out";

  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4" data-testid="coverage-card">
      <div className="flex items-center gap-2">
        <span className="grid place-items-center h-9 w-9 rounded-xl bg-brand/10 text-brand"><ListChecks size={18} /></span>
        <div className="min-w-0">
          <p className="font-heading font-bold text-navy text-sm flex items-center gap-1">Evidence Coverage <InfoTip topic="documentationCoverage" /></p>
          <p className="text-[11px] text-[#64748B]">{phaseLabel} documentation</p>
        </div>
      </div>

      <p className="mt-3 font-heading text-lg font-bold text-navy" data-testid="coverage-card-headline">
        {s.denominator_zero
          ? "0 applicable recommended views"
          : `Captured ${s.captured_views} of ${s.applicable_views} recommended views`}
        {!s.denominator_zero && <span className="text-brand"> — {s.percentage}%</span>}
      </p>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#475569]">
        <span data-testid="coverage-card-complete">{s.rooms_complete} rooms complete</span>
        <span data-testid="coverage-card-attention">{s.rooms_needing_attention} need attention</span>
        {s.missing_views > 0 && <span data-testid="coverage-card-missing">{s.missing_views} missing views</span>}
      </div>

      <p className="mt-2 text-[11px] text-[#94A3B8] leading-snug">
        Coverage measures completion of TenantSeal’s recommended photo views. It does not determine legal sufficiency or dispute outcomes.
      </p>

      <Button data-testid="coverage-card-review" onClick={() => navigate(`/coverage/${propertyId}`)}
        className="mt-3 w-full h-10 rounded-xl bg-navy hover:bg-navy-hover text-white">
        Review coverage <ArrowRight size={15} className="ml-1.5" />
      </Button>
    </div>
  );
}
